import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CreditCard, Download, HandHeart, Lock, Mail, ShieldCheck, Tag, Trash2, User } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import { localeOf } from '../../assets/js/locale';
import FormField, { PasswordField } from '../common/FormField';
import Loader from '../common/Loader';
import Modal from '../common/Modal';
import SectionEyebrow from '../common/SectionEyebrow';
import { notifyUserUpdated } from '../../assets/js/userEvents';
import { clearSession } from '../../utils/session';
import '../../assets/styles/ChooseSurvey.css';
import '../../assets/styles/TemplateBuilder.css';
import '../../assets/styles/ProfileArea.css';
import { accountPlan } from '../Pricing/plans';

// Se asume que el usuario está logueado y que hay una manera de identificarlo
// en el backend a través de la sesión o un token de autenticación (ej. JWT).
// Para este ejemplo, simularemos un ID de usuario fijo y un token para demostración.

// CAMBIO REALIZADO AQUÍ: Actualizado a una URL absoluta para el backend
const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api`; // La URL base de tu API de backend

const ProfilePage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [userData, setUserData] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);



  // Estado para controlar qué sección de edición está visible
  const [showEditSection, setShowEditSection] = useState(null); // 'name', 'email', 'organization', 'password', or null

  // Estados para la verificación de email
  const [emailVerificationStep, setEmailVerificationStep] = useState(1); // 1: input email, 2: verification code
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [emailVerificationMessage, setEmailVerificationMessage] = useState('');

  // Estados para gestión de suscripciones
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [ongRequest, setOngRequest] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionAction, setSubscriptionAction] = useState(''); // 'cancel', 'change'

  // Estados para gestión de códigos de descuento
  const [discountCode, setDiscountCode] = useState('');

  // RGPD: descargar mis datos y eliminar la cuenta
  const [exportingData, setExportingData] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');

  // Función para obtener los datos del usuario
  const fetchUserData = async () => {
    setLoading(true);
    setError(null);

    const getToken = () => {
      return localStorage.getItem('token');
    };
    const token = getToken();
    if (!token) {
      console.warn('No hay token de autenticación disponible.');
      return;
    }
    try {
      // Endpoint para obtener los datos del usuario actual
      const response = await fetch(`${API_BASE_URL}/user-profile`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, // Envía el token de autenticación
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setUserData(data);
    } catch (err) {
      console.error('Error al obtener los datos del usuario:', err);
      setError(t('profile_error_load'));
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener el token (definida a nivel de componente)
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Estado de la solicitud de ONG, para decir si está pendiente o por qué se rechazó
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${process.env.REACT_APP_API_URL}/ong-requests/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then(setOngRequest)
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchUserData();
  }, []); // Cargar datos del usuario al montar el componente

  // Función genérica para manejar la actualización de campos
  const handleUpdate = async (field, value) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    const token = getToken();
    if (!token) {
      console.warn('No hay token de autenticación disponible.');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/profile/${field}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ [field]: value }), // Envía el campo y su nuevo valor
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `${t('profile_update_error')} ${field}`);
      }

      const data = await response.json();
      setUserData((prevData) => ({ ...prevData, [field]: value })); // Actualiza el estado local
      notifyUserUpdated({ [field]: value });
      setSuccessMessage(data.message || `${field} ${t('profile_update_success')}`);
      setShowEditSection(null); // Ocultar el formulario de edición
    } catch (err) {
      console.error(`Error al actualizar ${field}:`, err);
      setError(err.message || `${t('profile_update_fail')} ${field}.`);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessMessage(null), 3000); // Limpiar mensaje de éxito después de 3 segundos
    }
  };

  // Función para manejar la actualización de la contraseña
  const handlePasswordUpdate = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    if (newPassword !== confirmNewPassword) {
      setError(t('profile_error_passwords_mismatch'));
      setLoading(false);
      return;
    }
    if (!currentPassword || !newPassword) {
      setError(t('profile_error_passwords_required'));
      setLoading(false);
      return;
    }
    if (newPassword.length < 6) {
      setError(t('profile_error_password_short'));
      setLoading(false);
      return;
    }
    const getToken = () => {
      return localStorage.getItem('token');
    };
    const token = getToken();
    if (!token) {
      console.warn('No hay token de autenticación disponible.');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/profile/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }), // Envía ambas contraseñas
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('profile_error_password_update'));
      }

      const data = await response.json();
      setSuccessMessage(data.message || t('profile_password_updated'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowEditSection(null); // Ocultar el formulario de edición
    } catch (err) {
      console.error('Error al actualizar la contraseña:', err);
      setError(err.message || t('profile_error_password_fail'));
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  // Función para manejar el cambio de email con verificación
  const handleEmailChange = async () => {
    if (!newEmail || newEmail.trim() === '') {
      setError(t('profile_error_email_required'));
      return;
    }

    // Validación básica de formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError(t('profile_error_email_format'));
      return;
    }

    setLoading(true);
    setError(null);
    setEmailVerificationMessage('');

    try {
      // Primero verificamos si el email ya existe
      console.log('Verificando si el email existe:', newEmail);
      const checkResponse = await fetch(`${process.env.REACT_APP_API_URL}/check-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newEmail }),
      });

      const checkData = await checkResponse.json();
      console.log('Respuesta del servidor:', checkData);

      if (checkResponse.ok && checkData.exists) {
        console.log('Email ya existe, mostrando error');
        setError(t('profile_error_email_exists'));
        setLoading(false);
        return;
      }

      if (!checkResponse.ok) {
        console.log('Error en la respuesta del servidor');
        setError(checkData.message || t('profile_error_email_verify'));
        setLoading(false);
        return;
      }

      console.log('Email no existe, procediendo con el envío del código');

      // Si el email no existe, enviamos el código de verificación
      const verificationResponse = await fetch(`${process.env.REACT_APP_API_URL}/verified`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newEmail, language }),
      });

      const verificationData = await verificationResponse.json();

      if (verificationResponse.ok) {
        setEmailVerificationMessage(t('profile_code_sent'));
        setEmailVerificationStep(2);
      } else {
        setError(verificationData.message || t('profile_error_send_code'));
      }
    } catch (err) {
      console.error('Error al verificar o enviar código de verificación:', err);
      setError(t('profile_error_email_change'));
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar el código y actualizar el email
  const handleEmailVerification = async () => {
    if (!verificationCode || verificationCode.trim() === '') {
      setError(t('profile_error_code_required'));
      return;
    }

    setLoading(true);
    setError(null);

    const getToken = () => {
      return localStorage.getItem('token');
    };
    const token = getToken();

    try {
      // El servidor comprueba el código y cambia el email en la misma llamada
      const updateResponse = await fetch(`${API_BASE_URL}/profile/email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email: newEmail, code: verificationCode.trim() }),
      });

      if (updateResponse.ok) {
        setUserData((prevData) => ({ ...prevData, email: newEmail }));
        notifyUserUpdated({ email: newEmail });
        setSuccessMessage(t('profile_email_updated'));
        setShowEditSection(null);
        setEmailVerificationStep(1);
        setNewEmail('');
        setVerificationCode('');
        setEmailVerificationMessage('');
      } else {
        const updateErrorData = await updateResponse.json().catch(() => ({}));
        setError(updateErrorData.message || t('profile_error_email_update'));
      }
    } catch (err) {
      console.error('Error al verificar el código:', err);
      setError(t('profile_error_code_verify'));
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };


  // Funciones para gestión de suscripciones
  const fetchSubscriptionData = async () => {
    const token = getToken();
    if (!token) return;

    try {
      // Primero intentar obtener datos específicos de suscripción
      const response = await fetch(`${API_BASE_URL}/user-subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionData(data);
      } else {
        // Si no funciona, usar los datos del usuario que ya tenemos
        if (userData && userData.has_permissions > 0) {
          setSubscriptionData({
            has_permissions: userData.has_permissions,
            subscription_status: 'active', // Asumir activa si tiene permisos
            stripe_customer_id: null,
            stripe_subscription_id: null
          });
        }
      }
    } catch (error) {
      console.error('Error al obtener datos de suscripción:', error);
      // Fallback: usar datos del usuario
      if (userData && userData.has_permissions > 0) {
        setSubscriptionData({
          has_permissions: userData.has_permissions,
          subscription_status: 'active',
          stripe_customer_id: null,
          stripe_subscription_id: null
        });
      }
    }
  };

  const handleCancelSubscription = async () => {
    const token = getToken();
    if (!token) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSuccessMessage(t('profile_sub_canceled'));
        fetchSubscriptionData(); // Recargar datos
        setShowSubscriptionModal(false);
      } else {
        const errorData = await response.json();
        setError(errorData.message || t('profile_error_sub_cancel'));
      }
    } catch (error) {
      setError(t('profile_error_sub_cancel'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangeSubscription = () => {
    // Redirigir a la página de suscripciones
    window.location.href = '/subscribe';
  };

  // Función para aplicar código de descuento
  const handleApplyDiscountCode = async () => {
    if (!discountCode || discountCode.trim() === '') {
      setError(t('profile_error_discount_required'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const token = getToken();
    if (!token) {
      console.warn('No hay token de autenticación disponible.');
      setLoading(false);
      return;
    }

    try {
      // El servidor comprueba el codigo, no deja bajar de plan ni tocar a los
      // administradores y, si pagaba con Stripe, cancela esa suscripcion.
      const response = await fetch(`${API_BASE_URL}/apply-discount-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ discountCode: discountCode.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('profile_error_apply_discount'));
      }

      const data = await response.json();
      setSuccessMessage(data.message || t('profile_discount_applied'));
      
      setDiscountCode('');
      setShowEditSection(null);
      
      // Recargar los datos del usuario y suscripción
      await fetchUserData();
      await fetchSubscriptionData();

    } catch (err) {
      console.error('Error al aplicar código de descuento:', err);
      setError(err.message || t('profile_error_apply_discount_fail'));
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessMessage(null), 3000);
    }
  };

  const getPlanName = (account) => (account.isRole
    ? t(`account_role_${account.key}`)
    : t(`profile_plan_${account.key}`));

  const getSubscriptionStatus = (status) => {
    switch (status) {
      case 'active': return t('profile_status_active');
      case 'canceled': return t('profile_status_canceled');
      case 'past_due': return t('profile_status_past_due');
      case 'incomplete': return t('profile_status_incomplete');
      case 'unpaid': return t('profile_status_unpaid');
      default: return t('profile_status_unknown');
    }
  };

  const profileDisplayName = userData?.name?.trim() || t('profile_my_profile');
  const profileEmail = userData?.email?.trim();

  // Cargar datos de suscripción cuando se monta el componente
  useEffect(() => {
    fetchSubscriptionData();
  }, [userData]); // Recargar cuando cambien los datos del usuario

  const initials = (profileDisplayName || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

  const handleExportData = async () => {
    setError(null);
    setSuccessMessage(null);
    setExportingData(true);
    try {
      const response = await fetch(`${API_BASE_URL}/profile/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('export');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `thepulse-mis-datos-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(t('profile_data_export_error'));
    } finally {
      setExportingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/profile`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ confirmEmail: deleteConfirmEmail })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const messages = {
          email_mismatch: 'profile_delete_email_mismatch',
          subscription_cancel_failed: 'profile_delete_subscription_failed'
        };
        setError(t(messages[data.code] || 'profile_delete_error'));
        return;
      }
      // Cuenta borrada: fuera la sesión y a la portada
      clearSession();
      window.dispatchEvent(new Event('authChange'));
      navigate('/', { replace: true });
    } catch (err) {
      setError(t('profile_delete_error'));
    } finally {
      setLoading(false);
    }
  };

  const closeSection = () => {
    setShowEditSection(null);
    setDeleteConfirmEmail('');
    setEmailVerificationStep(1);
    setNewEmail('');
    setVerificationCode('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setError(null);
  };

  if (loading && !userData) {
    return (
      <div className="tp-choose tp-profile tp-profile--center">
        <Loader />
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="tp-choose tp-profile tp-profile--center">
        <section className="tp-builder__panel tp-profile__state">
          <p className="tp-form__alert" role="alert">{error}</p>
          <button type="button" className="btn btn--primary" onClick={fetchUserData}>
            {t('profile_retry')}
          </button>
        </section>
      </div>
    );
  }

  const account = subscriptionData ? accountPlan(subscriptionData) : null;

  // Marcar "somos una ONG" al registrarse solo es lo que dice la persona; lo que manda es
  // el estado de la solicitud: en revisión, aprobada o rechazada.
  const ongDeclarada = Boolean(userData && Number(userData.is_ong) === 1);
  const ongEstado = ongRequest ? ongRequest.estado : null;
  const ongFecha = (value) => new Date(value).toLocaleDateString(localeOf(language), { day: 'numeric', month: 'long' });

  let ongValue;
  let ongAction;
  if (ongEstado === 'pendiente') {
    ongValue = t('profile_ong_pending').replace('{date}', ongFecha(ongRequest.created_at));
    ongAction = null; // Ya está enviada: nada que hacer hasta que se revise
  } else if (ongEstado === 'aprobada') {
    ongValue = t('profile_ong_verified');
    ongAction = null;
  } else if (ongEstado === 'rechazada') {
    ongValue = `${t('profile_ong_rejected')}${ongRequest.motivo ? ` ${ongRequest.motivo}` : ''}`;
    ongAction = t('profile_ong_retry');
  } else {
    ongValue = t(ongDeclarada ? 'profile_ong_marked' : 'profile_ong_desc');
    ongAction = t(ongDeclarada ? 'profile_ong_send_docs' : 'profile_ong_request');
  }

  const rows = [
    {
      key: 'name',
      icon: <User size={18} strokeWidth={1.9} />,
      label: t('profile_name'),
      value: profileDisplayName,
      action: t('profile_change')
    },
    {
      key: 'email',
      icon: <Mail size={18} strokeWidth={1.9} />,
      label: t('profile_email'),
      value: profileEmail || '—',
      action: t('profile_change')
    },
    {
      key: 'organization',
      icon: <Building2 size={18} strokeWidth={1.9} />,
      label: t('profile_organization'),
      value: (userData && userData.organization) || t('profile_org_not_specified'),
      action: t('profile_change')
    },
    {
      key: 'password',
      icon: <Lock size={18} strokeWidth={1.9} />,
      label: t('profile_password'),
      value: t('profile_password_hidden'),
      action: t('profile_change')
    },
    {
      key: 'discount',
      icon: <Tag size={18} strokeWidth={1.9} />,
      label: t('profile_discount_code'),
      value: account && account.isRole ? t('profile_discount_not_needed') : t('profile_discount_desc'),
      action: account && account.isRole ? null : t('profile_apply')
    },
    // Quien no marco "somos una ONG" al registrarse puede pedirlo mas tarde
    ...(account && account.isRole ? [] : [{
      key: 'ong',
      icon: <HandHeart size={18} strokeWidth={1.9} />,
      label: t('profile_ong'),
      value: ongValue,
      action: ongAction,
      to: ongAction ? '/ong-verification' : undefined
    }])
  ];

  const hasSubscription = Boolean(account && (account.isRole || account.isPaid || account.viaCode || subscriptionData.stripe_subscription_id));

  return (
    <div className="tp-choose tp-profile">
      <header className="tp-choose__head">
        <div className="tp-choose__heading">
          <SectionEyebrow>{t('profile_manage_kicker')}</SectionEyebrow>
          <h1 className="tp-choose__title">{t('profile_manage_title')}</h1>
          <p className="tp-choose__lead">{t('profile_manage_subtitle')}</p>
        </div>
      </header>

      <section className="tp-profile__identity">
        <span className="tp-profile__avatar" aria-hidden="true">{initials}</span>
        <div>
          <p className="tp-profile__name">{profileDisplayName}</p>
          {profileEmail && <p className="tp-profile__mail">{profileEmail}</p>}
        </div>
      </section>

      {successMessage && <p className="tp-profile__ok" role="status">{successMessage}</p>}
      {error && !showEditSection && <p className="tp-form__alert" role="alert">{error}</p>}

      <section className="tp-builder__panel">
        <ul className="tp-profile__rows">
          {rows.map((row) => (
            <li key={row.key} className="tp-profile__row">
              <span className="tp-profile__icon" aria-hidden="true">{row.icon}</span>
              <div className="tp-profile__body">
                <p className="tp-profile__label">{row.label}</p>
                <p className="tp-profile__value">{row.value}</p>
              </div>
              {row.action && (
                <button
                  type="button"
                  className="btn btn--outline btn--sm"
                  onClick={() => {
                    if (row.to) {
                      navigate(row.to);
                      return;
                    }
                    setError(null);
                    setSuccessMessage(null);
                    setShowEditSection(row.key);
                  }}
                >
                  {row.action}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="tp-builder__panel tp-profile__subscription">
        <div className="tp-builder__panel-head">
          <h2 className="tp-builder__count">
            <CreditCard size={16} strokeWidth={2} aria-hidden="true" />
            {t('profile_subscription')}
          </h2>
        </div>

        {hasSubscription ? (
          <>
            <dl className="tp-profile__plan">
              <div>
                <dt>{t('profile_current_plan')}</dt>
                <dd>{getPlanName(account)}</dd>
              </div>
              <div>
                <dt>{t('profile_status')}</dt>
                <dd>
                  {account.isRole
                    ? t('account_full_access')
                    : account.viaCode
                      ? (account.expiresAt
                        ? t('account_via_code_until').replace('{date}', account.expiresAt.toLocaleDateString(localeOf(language), { day: 'numeric', month: 'long', year: 'numeric' }))
                        : t('account_via_code'))
                      : getSubscriptionStatus(subscriptionData.subscription_status)}
                </dd>
              </div>
              {subscriptionData.stripe_subscription_id && (
                <div>
                  <dt>{t('profile_subscription_id')}</dt>
                  <dd className="tp-profile__plan-id">{subscriptionData.stripe_subscription_id}</dd>
                </div>
              )}
            </dl>

            {account.isRole ? (
              <p className="tp-profile__role-note">{t('account_role_note')}</p>
            ) : (
              <div className="tp-builder__panel-actions">
                {account.isPaid ? (
                  <button
                    type="button"
                    className="btn btn--sm tp-builder__delete"
                    onClick={() => { setSubscriptionAction('cancel'); setShowSubscriptionModal(true); }}
                  >
                    {t('profile_cancel_subscription')}
                  </button>
                ) : <span />}
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => { setSubscriptionAction('change'); setShowSubscriptionModal(true); }}
                >
                  {t('profile_change_plan')}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="tp-profile__no-plan">
            <p>{t('profile_no_subscription')}</p>
            <button type="button" className="btn btn--primary" onClick={handleChangeSubscription}>
              {t('profile_subscribe')}
            </button>
          </div>
        )}
      </section>

      <section className="tp-builder__panel tp-profile__data">
        <div className="tp-builder__panel-head">
          <h2 className="tp-builder__count">
            <ShieldCheck size={16} strokeWidth={2} aria-hidden="true" />
            {t('profile_data_title')}
          </h2>
        </div>
        <ul className="tp-profile__rows">
          <li className="tp-profile__row">
            <span className="tp-profile__icon" aria-hidden="true"><Download size={18} strokeWidth={1.9} /></span>
            <div className="tp-profile__body">
              <p className="tp-profile__label">{t('profile_data_export_label')}</p>
              <p className="tp-profile__value">{t('profile_data_export_desc')}</p>
            </div>
            <button type="button" className="btn btn--outline btn--sm" onClick={handleExportData} disabled={exportingData}>
              {exportingData ? t('profile_data_exporting') : t('profile_data_export_button')}
            </button>
          </li>
          <li className="tp-profile__row">
            <span className="tp-profile__icon" aria-hidden="true"><Trash2 size={18} strokeWidth={1.9} /></span>
            <div className="tp-profile__body">
              <p className="tp-profile__label">{t('profile_delete_label')}</p>
              <p className="tp-profile__value">{t('profile_delete_desc')}</p>
            </div>
            <button
              type="button"
              className="btn btn--sm tp-builder__delete"
              onClick={() => { setError(null); setSuccessMessage(null); setShowEditSection('deleteAccount'); }}
            >
              {t('profile_delete_button')}
            </button>
          </li>
        </ul>
      </section>

      <Modal
        open={showEditSection === 'deleteAccount'}
        onOpenChange={(open) => { if (!open) closeSection(); }}
        variant="delete"
        title={t('profile_delete_label')}
        description={t('profile_delete_modal_desc')}
        cancelText={t('profile_cancel')}
        confirmText={loading ? t('profile_deleting') : t('profile_delete_confirm')}
        // Solo con el email de la cuenta escrito: evita borrarla por un clic
        confirmDisabled={loading || !profileEmail || deleteConfirmEmail.trim().toLowerCase() !== String(profileEmail).trim().toLowerCase()}
        onCancel={closeSection}
        onConfirm={() => { handleDeleteAccount(); return false; }}
      >
        <div className="tp-form tp-form--bare">
          <FormField
            id="profile-delete-email"
            label={t('profile_delete_confirm_label')}
            type="email"
            value={deleteConfirmEmail}
            placeholder={profileEmail || ''}
            autoComplete="off"
            onChange={(e) => { setDeleteConfirmEmail(e.target.value); setError(null); }}
          />
          {error && <p className="tp-form__alert" role="alert">{error}</p>}
        </div>
      </Modal>

      <Modal
        open={showEditSection === 'name'}
        onOpenChange={(open) => { if (!open) closeSection(); }}
        variant="edit"
        title={t('profile_name')}
        cancelText={t('profile_cancel')}
        confirmText={loading ? t('profile_saving') : t('profile_save_name')}
        confirmDisabled={loading}
        onCancel={closeSection}
        onConfirm={() => { handleUpdate('name', userData.name); return false; }}
      >
        <div className="tp-form tp-form--bare">
          <FormField
            id="profile-name"
            label={t('profile_name')}
            type="text"
            value={(userData && userData.name) || ''}
            placeholder={t('profile_new_name_placeholder')}
            onChange={(e) => setUserData({ ...userData, name: e.target.value })}
          />
          {error && <p className="tp-form__alert" role="alert">{error}</p>}
        </div>
      </Modal>

      <Modal
        open={showEditSection === 'email'}
        onOpenChange={(open) => { if (!open) closeSection(); }}
        variant="edit"
        title={t('profile_email')}
        description={emailVerificationMessage || undefined}
        cancelText={t('profile_cancel')}
        confirmText={loading
          ? t('profile_verifying')
          : emailVerificationStep === 1 ? t('profile_verify_email') : t('profile_confirm_change')}
        confirmDisabled={loading}
        onCancel={closeSection}
        onConfirm={() => {
          if (emailVerificationStep === 1) handleEmailChange();
          else handleEmailVerification();
          return false;
        }}
      >
        <div className="tp-form tp-form--bare">
          {emailVerificationStep === 1 ? (
            <FormField
              id="profile-email"
              label={t('profile_email')}
              type="email"
              value={newEmail}
              placeholder={t('profile_new_email_placeholder')}
              onChange={(e) => { setNewEmail(e.target.value); setError(null); }}
            />
          ) : (
            <FormField
              id="profile-code"
              label={t('profile_verification_code_placeholder')}
              type="text"
              value={verificationCode}
              placeholder={t('profile_verification_code_placeholder')}
              onChange={(e) => { setVerificationCode(e.target.value); setError(null); }}
            />
          )}
          {error && <p className="tp-form__alert" role="alert">{error}</p>}
        </div>
      </Modal>

      <Modal
        open={showEditSection === 'organization'}
        onOpenChange={(open) => { if (!open) closeSection(); }}
        variant="edit"
        title={t('profile_organization')}
        cancelText={t('profile_cancel')}
        confirmText={loading ? t('profile_saving') : t('profile_save_org')}
        confirmDisabled={loading}
        onCancel={closeSection}
        onConfirm={() => { handleUpdate('organization', userData.organization); return false; }}
      >
        <div className="tp-form tp-form--bare">
          <FormField
            id="profile-org"
            label={t('profile_organization')}
            type="text"
            value={(userData && userData.organization) || ''}
            placeholder={t('profile_new_org_placeholder')}
            onChange={(e) => setUserData({ ...userData, organization: e.target.value })}
          />
          {error && <p className="tp-form__alert" role="alert">{error}</p>}
        </div>
      </Modal>

      <Modal
        open={showEditSection === 'password'}
        onOpenChange={(open) => { if (!open) closeSection(); }}
        variant="edit"
        title={t('profile_password')}
        cancelText={t('profile_cancel')}
        confirmText={loading ? t('profile_saving') : t('profile_save_password')}
        confirmDisabled={loading}
        onCancel={closeSection}
        onConfirm={() => { handlePasswordUpdate(); return false; }}
      >
        <div className="tp-form tp-form--bare">
          <PasswordField
            id="profile-current-password"
            label={t('profile_current_password')}
            value={currentPassword}
            onChange={(e) => { setCurrentPassword(e.target.value); setError(null); }}
          />
          <PasswordField
            id="profile-new-password"
            label={t('profile_new_password')}
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setError(null); }}
          />
          <PasswordField
            id="profile-confirm-password"
            label={t('profile_confirm_new_password')}
            value={confirmNewPassword}
            onChange={(e) => { setConfirmNewPassword(e.target.value); setError(null); }}
          />
          {error && <p className="tp-form__alert" role="alert">{error}</p>}
        </div>
      </Modal>

      <Modal
        open={showEditSection === 'discount'}
        onOpenChange={(open) => { if (!open) closeSection(); }}
        variant="edit"
        title={t('profile_discount_code')}
        description={t('profile_discount_desc')}
        cancelText={t('profile_cancel')}
        confirmText={loading ? t('profile_applying') : t('profile_apply_code')}
        confirmDisabled={loading}
        onCancel={closeSection}
        onConfirm={() => { handleApplyDiscountCode(); return false; }}
      >
        <div className="tp-form tp-form--bare">
          <FormField
            id="profile-discount"
            label={t('profile_discount_code')}
            type="text"
            value={discountCode}
            placeholder={t('profile_discount_placeholder')}
            onChange={(e) => { setDiscountCode(e.target.value); setError(null); }}
          />
          {error && <p className="tp-form__alert" role="alert">{error}</p>}
        </div>
      </Modal>

      <Modal
        open={showSubscriptionModal}
        onOpenChange={(open) => { if (!open) setShowSubscriptionModal(false); }}
        variant={subscriptionAction === 'cancel' ? 'delete' : 'edit'}
        title={subscriptionAction === 'cancel' ? t('profile_cancel_subscription') : t('profile_change_plan')}
        description={subscriptionAction === 'cancel' ? t('profile_cancel_sub_confirm') : t('profile_change_plan_desc')}
        cancelText={t('profile_cancel')}
        confirmText={loading ? t('profile_processing') : (subscriptionAction === 'cancel' ? t('profile_confirm') : t('profile_change_plan'))}
        confirmDisabled={loading}
        onCancel={() => setShowSubscriptionModal(false)}
        onConfirm={() => {
          if (subscriptionAction === 'cancel') handleCancelSubscription();
          else handleChangeSubscription();
          return false;
        }}
      />
    </div>
  );
};

export default ProfilePage;
